// src/stores/themeStore.js
import { writable, derived } from 'svelte/store';

// Check if we're in a browser environment
const browser = typeof window !== 'undefined';

// Theme definitions
export const themes = {
  light: 'light',
  dark: 'dark',
  classic: 'classic'
};

// Local storage key
const THEME_STORAGE_KEY = 'patdraft-theme';

// Get initial theme from localStorage or default to light
function getInitialTheme() {
  if (browser) {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved && themes[saved]) {
      return saved;
    }
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return themes.dark;
    }
  }
  return themes.classic;
}

// Create the theme store
function createThemeStore() {
  const { subscribe, set, update } = writable(getInitialTheme());

  return {
    subscribe,
    
    setTheme: (theme) => {
      if (!themes[theme]) {
        console.error(`Invalid theme: ${theme}`);
        return;
      }
      
      set(theme);
      
      // Save to localStorage
      if (browser) {
        localStorage.setItem(THEME_STORAGE_KEY, theme);
        
        // Apply theme to document
        document.documentElement.setAttribute('data-theme', theme);
      }
    },
    
    toggleTheme: () => {
      update(currentTheme => {
        let newTheme;
        // Cycle through: light -> dark -> classic -> light
        if (currentTheme === themes.light) {
          newTheme = themes.dark;
        } else if (currentTheme === themes.dark) {
          newTheme = themes.classic;
        } else {
          newTheme = themes.light;
        }
        
        // Save to localStorage
        if (browser) {
          localStorage.setItem(THEME_STORAGE_KEY, newTheme);
          
          // Apply theme to document
          document.documentElement.setAttribute('data-theme', newTheme);
        }
        
        return newTheme;
      });
    },
    
    init: () => {
      if (browser) {
        const theme = getInitialTheme();
        document.documentElement.setAttribute('data-theme', theme);
        
        // Listen for system theme changes
        if (window.matchMedia) {
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
          mediaQuery.addEventListener('change', (e) => {
            const systemTheme = e.matches ? themes.dark : themes.light;
            // Only apply system theme if user hasn't set a preference
            if (!localStorage.getItem(THEME_STORAGE_KEY)) {
              themeStore.setTheme(systemTheme);
            }
          });
        }
      }
    }
  };
}

export const themeStore = createThemeStore();

// Derived store for convenient boolean checks
export const isDarkTheme = derived(
  themeStore,
  $theme => $theme === themes.dark
);

// Derived store for theme-specific classes
export const themeClasses = derived(
  themeStore,
  $theme => ({
    light: $theme === themes.light,
    dark: $theme === themes.dark,
    classic: $theme === themes.classic
  })
);